import {useOverlayActivity} from "components/useOverlayActivity";
import React, {useEffect, useRef, useState} from "react";
import {Avatar, Divider, IconButton, List, Popover} from "@mui/material";
import {useUser} from "UserProvider";
import {canUpgrade, planOf, uploadCapBytes} from "home/gating";
import {useHistory} from "utils/HistoryUtils";
import {useLocale, useMessages} from "i18n";
import {useLogout} from "./useLogout";
import {useInlineName} from "./useInlineName";
import {AccountApi} from "account/AccountApi";
import {AnalyticsApi} from "account/AnalyticsApi";
import {isTrialPlan} from "account/trial";
import formatBytes from "../../core/format/byteFormat";
import {usePricingUpgrade} from "pricing/usePricingUpgrade";
import {PricingDialog} from "pricing/PricingDialog";
import {UpgradeCheckoutDialog} from "pay/UpgradeCheckoutDialog";
import {PlanChangeDialog} from "pricing/PlanChangeDialog";
import {useAppNavigation} from "nav/useAppNavigation";
import {collapsibleNavItem} from "nav/navCollapse";
import {
	AccountIcon,
	AdminIcon,
	AnalyticsIcon,
	BillingIcon,
	BrandingIcon,
	DESTINATION_ICONS,
	LogoutIcon,
	UpgradeIcon,
} from "./icons";
import {profileMenuActions, type ProfileMenuActionKey} from "./profileMenuActions";
import {ADMIN_ROUTE} from "account/admin/adminRoute";
import {
	classifyRenderedFailure,
	reportRenderedFailure,
	RenderedFailureKey,
} from "utils/renderedFailure";
import {
	AddNameButton,
	CollapsedNavRow,
	CollapsedNavSection,
	IdentityRow,
	IdentityText,
	MenuRow,
	Name,
	NameInput,
	PastDueBadge,
	PastDueNotice,
	PlanChip,
	PlanChipRow,
	PlanChips,
	Root,
	RowIcon,
	Secondary,
	TrialChip,
	UpgradePill,
	UpgradeRow,
	UsageBlock,
	UsageCaption,
	UsageLabel,
	UsageValue,
	avatarSx,
	paperSx,
	triggerSx,
} from "./ProfileMenu.styles";
import BaseButton from "components/BaseButton";
import {openBillingPortal} from "pay/PayApi";
import {subscriptionDisplayName} from "account/subscription";

/**
 * Account popover for the logged-in top navigation (ticket #11, restyled to v3; moved from the rail
 * into the top bar in ui#96).
 *
 * The avatar opens a v3 card popover with:
 *  - the signed-in identity (name, company/email) plus a plan chip — gold paid-plan pill for an active
 *    subscription, neutral chip for Free; every non-terminal tier also gets an
 *    Upgrade pill beneath it (ui#167, ui#189) opening the {@link PricingDialog}. When a referral
 *    window rather than a subscription is what answers those plan fields, a neutral TRIAL chip sits
 *    beside the tier chip (ui#1441) — the gold pill is otherwise indistinguishable from a paid Basic,
 *    which is precisely how a trial user read it. The signal is `/account`'s `trialExpiresAt` through
 *    `account/trial.isTrialPlan`, the one field the api sets ONLY for a trial-answered plan;
 *  - an honest usage indicator: there is no per-user storage quota in the product, so instead of
 *    inventing one we show the real lifetime "Total sent" figure from `GET /analytics/summary`
 *    (`totalBytes`, the same metric as the Account → Analytics tile) with the plan's per-transfer
 *    upload cap (gating.ts) as context;
 *  - quick actions: Upgrade (Free, Basic and Plus), Account, Analytics (ui#1522 — the account's
 *    embedded dashboards, which used to be a tab on the account page and now open as a sheet of their
 *    own), Billing (ui#1791 — the account page's other tab, moved here the same way and placed directly
 *    under Analytics on the owner's instruction), Admin (ui#1882: a shortcut to the operator console
 *    at `/account/admin`, offered only when `/account` reported the caller's own role and placed
 *    directly above Logout; the server still guards the page with a 403, so this is convenience, not
 *    access control) and Logout, rendered from the
 *    {@link profileMenuActions} table so their ORDER is a testable list rather than the order of JSX
 *    siblings — the owner's #520 rule is "Logout is the last row, always", and this repo has no
 *    UI-component tests that could otherwise catch a reorder. Being quick actions they are FIXED
 *    furniture: unlike the collapsed destinations below they never leave the menu, and they have no
 *    counterpart in the top-bar pill, so they are absent from the ui#451 collapse table by design
 *    rather than omission;
 *  - on a narrow viewport, the top-bar pill items that no longer fit there (ui#451) — the same
 *    treatment ui#117 gave Language. They render from the SAME `nav.destinations` / `nav.branding`
 *    catalog entries, the same {@link useAppNavigation} model and the same {@link DESTINATION_ICONS}
 *    glyphs the pill uses, under media queries that are the exact complement of the pill's, so an item
 *    shows in one surface or the other. Personalization's is a BAND (641–879px, ui#452): on a phone it
 *    keeps its own square in the bar, so its row here is absent below 641px as well as above 839px.
 *    Its row is written out below rather than coming from the `collapsedDestinations` map, even though
 *    ui#1292 made it an {@link useAppNavigation} destination: the collapse table keys it as `branding`
 *    (`navCollapse`, whose measured thresholds predate the route) and it opens through the focus-return
 *    handoff below rather than through `goTo`. Mapping it as well would render the row twice.
 *
 * Every row carries a leading glyph (#520). The glyphs are DECORATION — `aria-hidden`, drawn in the
 * shared `nav/pro/icons` stroke conventions — so each row's accessible name is still its text alone,
 * which is what the ui#451 reachability spec matches on.
 *
 * There is no Support row here any more (#520). Support used to exist twice — this row opening the
 * authenticated request flow, and the Resources menu's row opening the public feedback form — so the
 * same intent led to two different forms depending on which surface you found first. The Resources
 * entry is now the single one, and it dispatches on auth state; see `nav/normal/NavBar.tsx`.
 *
 * There is no "Advertise with us" row either (owner 2026-08-17). It lived here and nowhere else, which
 * put an offer aimed at strangers behind a login; it is now a column of its own in the Resources menu,
 * rendered in both auth states, pointing at the `/advertising` presentation page rather than dropping
 * a first-time buyer straight into the booking form.
 *
 * The Upgrade affordances (row + pill) are shown for tiers with a higher self-serve plan available
 * (Free → Basic → Plus → Premium) and open the shared {@link PricingDialog} / signed-in
 * {@link usePricingUpgrade} checkout flow, exactly as the logged-out NavBar branch does (epic #141,
 * ui#167). Reaching this component already implies logged-in + post-mount (NavBar gates it behind
 * `useIsLoggedIn`), so the dialogs mount hydration-safely.
 *
 * The avatar picture (`AccountApi.getAccount().profilePictureURL`) and the usage summary load
 * lazily each time the popover opens, so the rail mounts without network chatter.
 */

/** First letters of the display name (or email), used as the avatar fallback. */
function initialsOf(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return "?";
	if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
	return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/**
 * Dumb inline editor rendered in the Name slot while adding a name (ui#139): Enter saves, Escape
 * cancels, and blurring away cancels too (unless the save is already in flight).
 */
function InlineNameEditor({busy, onSave, onCancel}: {
	busy: boolean;
	onSave: (value: string) => void;
	onCancel: () => void;
}) {
	const [value, setValue] = useState("");
	const {account} = useMessages();
	return (
		<NameInput
			value={value}
			onChange={(e) => setValue(e.target.value)}
			onKeyDown={(e) => {
				if (e.key === "Enter") onSave(value);
				if (e.key === "Escape") onCancel();
			}}
			onBlur={() => {
				if (!busy) onCancel();
			}}
			placeholder={account.menu.namePlaceholder}
			aria-label={account.menu.nameAria}
			disabled={busy}
			autoFocus
			spellCheck={false}
		/>
	);
}

/** The usage figure loads per popover open: loading → loaded(totalBytes) | error. */
type UsageState = {status: "loading"} | {status: "error"} | {status: "loaded"; totalBytes: number};

export interface ProfileMenuProps {
	/**
	 * Opens the personalization editor, which since ui#1292 means NAVIGATING to `/personalization`.
	 *
	 * The row still calls back into the NavBar rather than routing itself, and the reason survived the
	 * move: the bar owns the single `BrandSheet` and, more to the point, the FOCUS RETURN TARGET. The
	 * element passed is where focus must land once the sheet closes; the clicked ROW is not a valid
	 * answer, because it unmounts with the popover, so this hands over the avatar trigger, which the bar
	 * keeps mounted at every width. A bare `goTo(PERSONALIZATION_ROUTE)` here would open the sheet just
	 * as well and drop a keyboard user at `<body>` on close.
	 */
	onOpenBranding: (restoreFocusTo: HTMLElement | null) => void;
	/**
	 * Opens the Analytics sheet (ui#1522), through the SAME deferred handoff the branding row uses and
	 * for the same reason: the row that raised it unmounts with the popover, so it cannot be the
	 * element focus returns to. The bar owns the sheet and is handed the durable avatar trigger.
	 *
	 * Unlike branding this opens no route — the analytics tab never had a URL, so there is no deep
	 * link to preserve and nothing for the CloudFront SPA prefix list to learn about.
	 */
	onOpenAnalytics: (restoreFocusTo: HTMLElement | null) => void;
	/**
	 * Opens the Billing sheet (ui#1791), through the SAME deferred handoff the branding and analytics rows
	 * use and for the same reason: the row that raised it unmounts with the popover, so it cannot be the
	 * element focus returns to. The bar owns the sheet and is handed the durable avatar trigger.
	 *
	 * Like analytics this opens no route — the billing tab it replaces was local state on the account page
	 * and never had a URL, so there is no deep link to preserve and nothing for the CloudFront SPA prefix
	 * list to learn about.
	 */
	onOpenBilling: (restoreFocusTo: HTMLElement | null) => void;
	/**
	 * Hands the avatar TRIGGER back to the bar (ui#1292).
	 *
	 * The bar needs a focus-return target that exists at every width, for the opens nothing on the bar
	 * initiated — a command-palette Navigate, a deep link, a typed URL. Its own Personalization pill
	 * cannot serve: between 641 and 879px the ui#452 collapse hides it with `display: none`, and a
	 * hidden element accepts `.focus()` by doing nothing. This trigger is the one control the bar keeps
	 * mounted and visible at every width — the same property that already makes it the branding row's
	 * return target below.
	 */
	onTriggerRef: (trigger: HTMLButtonElement | null) => void;
}

export default function ProfileMenu({
	onOpenBranding, onOpenAnalytics, onOpenBilling, onTriggerRef,
}: ProfileMenuProps) {
	const [user] = useUser();
	const history = useHistory();
	const {pricing, account, banner, common, nav} = useMessages();
	const {destinations} = useAppNavigation();
	const {locale} = useLocale();
	const performLogout = useLogout();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	// The avatar button, which outlives every popover open/close — the durable focus target the
	// deferred branding handoff below restores to (`anchorEl` is cleared as soon as the menu closes).
	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const [pictureURL, setPictureURL] = useState<string | undefined>(undefined);
	const [usage, setUsage] = useState<UsageState>({status: "loading"});
	const [pricingOpen, setPricingOpen] = useState(false);
	// Same signed-in upgrade flow the logged-out NavBar branch mounts (ui#167): a paid CTA opens the
	// tiered checkout dialog, while Free ("Start free") routes to /account. Catalogue resolution stays
	// at the checkout boundary in useStartCheckout/PricingApi.
	// Its only door is the pricing dialog below, so every checkout here is the plan picker's.
	const {onUpgrade, checkout, closeCheckout, planChange} = usePricingUpgrade("PLAN_UPGRADE");

	const open = Boolean(anchorEl);
	const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
	const handleClose = () => setAnchorEl(null);

	// ui#139: nameless accounts (passwordless provisioning) get an inline "Add your name" editor
	// instead of a broken fallback; the hook also filters the legacy "null null" serialization.
	const inlineName = useInlineName();
	const name = inlineName.name || user.email || account.menu.fallbackName;
	const secondary = user.company || user.email;
	const plan = planOf(user);
	const planName = subscriptionDisplayName(user.subscriptionType, pricing.planNames);
	const showUpgrade = canUpgrade(plan);
	/**
	 * ui#1441. `isTrialPlan` and NOT `liveTrialExpiry`: the chip qualifies whatever the tier chip
	 * beside it is claiming, and both are read off the SAME persisted `User`. Applying the clock here
	 * would drop the qualifier from a stale rehydrated window while leaving "BASIC" standing — which is
	 * the exact "a trial looks like a paid plan" reading this chip exists to remove. Being clock-free
	 * it is also hydration-safe by construction, so the nav needs no post-mount gate for it.
	 */
	const onTrial = isTrialPlan(user.trialExpiresAt);
	const capBytes = uploadCapBytes(plan);
	// Not a Premium special case — every baked plan is finite today (Premium is 1 TB). The cap goes
	// non-finite only via the API's "null = unlimited" contract (`PlanLimits.maxHostedUploadBytes` is
	// `integer|null`), which `planEntitlements.unlimitedAsInfinity` maps to Infinity. Guards against
	// rendering "Infinity GB" if the backend ever bakes a null allowance for this plan.
	const capCaption = Number.isFinite(capBytes)
		? account.menu.capCaption(formatBytes(capBytes, locale, 0))
		: account.menu.capUnlimited;

	// Load the avatar picture and the usage summary lazily, fresh on every open. The two fetches
	// fail independently; `active` guards against a setState racing a close/unmount.
	useEffect(() => {
		if (!open) return;
		let active = true;
		AccountApi.getAccount()
			.then((account) => {
				if (active) setPictureURL(account.profilePictureURL || undefined);
			})
			.catch((e) => reportRenderedFailure(
				"profile_menu_account_load",
				classifyRenderedFailure(e, RenderedFailureKey.LoadFailed),
			));
		setUsage({status: "loading"});
		AnalyticsApi.getSummary()
			.then((summary) => {
				if (active) setUsage({status: "loaded", totalBytes: summary.totalBytes});
			})
			.catch((e) => {
				reportRenderedFailure(
					"profile_menu_usage_load",
					classifyRenderedFailure(e, RenderedFailureKey.LoadFailed),
				);
				if (active) setUsage({status: "error"});
			});
		return () => {
			active = false;
		};
	}, [open]);

	const goTo = (path: string) => {
		handleClose();
		history.push(path);
	};
	const goToAccount = () => goTo("/account");
	const handleLogout = () => {
		handleClose();
		performLogout();
	};
	// Close the popover, then open the pricing sheet: the dialog is mounted at the Root level (below,
	// a sibling of the Popover), so dismissing the popover does NOT unmount the sheet mid-flow.
	const openUpgrade = () => {
		handleClose();
		setPricingOpen(true);
	};
	/**
	 * Branding cannot use openUpgrade's plain close-then-open. MUI captures whatever is focused when a
	 * modal opens and returns focus there on close; here that would be the row just clicked, which
	 * unmounts with the popover moments later, so closing the sheet dropped focus to `<body>` and left
	 * a keyboard user outside the bar entirely.
	 *
	 * Focusing the avatar first does NOT fix it either — while the popover is still open its focus trap
	 * pulls focus straight back inside. So two things happen: the open is DEFERRED to the popover's
	 * `onExited` (the same handoff NavBar uses for its resource dialog) so only one modal is ever open
	 * and no trap fights another, and the durable avatar trigger is handed to the bar as the element to
	 * restore focus to, which `BrandSheet`'s `onTransitionExited` honours instead of MUI's default.
	 */
	// WHICH deferred open is waiting, not merely whether one is (ui#1522 added the second, ui#1791 the
	// third). A boolean per sheet would let two of them be true at once and open both on one exit.
	type SheetHandoff = "branding" | "analytics" | "billing";
	const pendingHandoffRef = useRef<SheetHandoff | null>(null);
	const openAfterExit = (handoff: SheetHandoff) => () => {
		pendingHandoffRef.current = handoff;
		handleClose();
	};
	const openBranding = openAfterExit("branding");
	// One table rather than a chain of ifs: a fourth sheet adds a line here and the compiler requires it,
	// where a forgotten `if` would leave a row that closes the popover and then does nothing at all.
	const handoffOpeners: Record<SheetHandoff, (opener: HTMLElement | null) => void> = {
		branding: onOpenBranding,
		analytics: onOpenAnalytics,
		billing: onOpenBilling,
	};
	const onPopoverExited = () => {
		const handoff = pendingHandoffRef.current;
		pendingHandoffRef.current = null;
		if (handoff !== null) {
			handoffOpeners[handoff](triggerRef.current);
		}
	};
	// The destinations that leave the pill on a narrow viewport (ui#451), in bar order. Account needs
	// no exclusion here the way the pill needs one: it is not a collapsible item, so it falls out with
	// everything else the bar never hands over. Each row carries the pill's own disabled state, so an
	// un-activated account (#12) cannot reach through this surface what the bar denies it.
	const observedExit = useOverlayActivity(open, onPopoverExited);
	const collapsedDestinations = destinations.flatMap(destination => {
		const collapse = collapsibleNavItem(destination.key);
		return collapse ? [{destination, collapse}] : [];
	});
	// The quick actions in menu order (#520). The order lives in `profileMenuActions` and is asserted
	// there; this map only says what each row DOES, so a row cannot exist without a place in that order.
	const actions = profileMenuActions({
		upgrade: common.upgrade,
		account: account.menu.account,
		analytics: account.menu.analytics,
		billing: account.menu.billing,
		admin: account.menu.admin,
		logout: account.menu.logout,
	}, {canUpgrade: showUpgrade, showsAdminShortcut: user.admin});
	const actionIcons: Record<ProfileMenuActionKey, React.ReactNode> = {
		upgrade: <UpgradeIcon/>,
		account: <AccountIcon/>,
		analytics: <AnalyticsIcon/>,
		billing: <BillingIcon/>,
		admin: <AdminIcon/>,
		logout: <LogoutIcon/>,
	};
	const actionHandlers: Record<ProfileMenuActionKey, () => void> = {
		upgrade: openUpgrade,
		account: goToAccount,
		analytics: openAfterExit("analytics"),
		billing: openAfterExit("billing"),
		admin: () => goTo(ADMIN_ROUTE),
		logout: handleLogout,
	};
	return (
		<Root>
			<IconButton
				ref={(node: HTMLButtonElement | null) => {
					triggerRef.current = node;
					onTriggerRef(node);
				}}
				onClick={handleOpen}
				size="small"
				aria-label={account.menu.triggerAria}
				aria-haspopup="true"
				aria-expanded={open}
				sx={triggerSx}
			>
				<Avatar src={pictureURL} alt={name} sx={avatarSx(36)}>
					{initialsOf(name)}
				</Avatar>
				{/* Conditionally MOUNTED, not hidden-in-place: the e2e negative case asserts count 0,
				    which an invisible-but-rendered dot would fail. */}
				{user.isPastDue() && <PastDueBadge data-testid="past-due-badge"/>}
			</IconButton>
			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{vertical: "bottom", horizontal: "right"}}
				transformOrigin={{vertical: "top", horizontal: "right"}}
				slotProps={{paper: {elevation: 0, sx: paperSx}, transition: {onExited: observedExit}}}
			>
				<IdentityRow>
					<Avatar src={pictureURL} alt={name} sx={avatarSx(40)}>
						{initialsOf(name)}
					</Avatar>
					<IdentityText>
						{inlineName.name !== null && <Name>{inlineName.name}</Name>}
						{inlineName.name === null && !inlineName.editing && (
							<AddNameButton type="button" onClick={inlineName.start}>{account.menu.addName}</AddNameButton>
						)}
						{inlineName.name === null && inlineName.editing && (
							<InlineNameEditor busy={inlineName.busy} onSave={inlineName.save} onCancel={inlineName.cancel}/>
						)}
						<Secondary>{secondary}</Secondary>
					</IdentityText>
					{/* Gold chip = paying plan (planOf), not account activation — access-first (#12). Tiers
					    below Premium get an Upgrade pill stacked beneath the chip (ui#167, ui#189), and a
					    trial-answered plan gets the TRIAL qualifier beside it (ui#1441). */}
					<PlanChips>
						<PlanChipRow>
							<PlanChip $pro={plan !== "free"}>{planName}</PlanChip>
							{onTrial && <TrialChip>{account.menu.trial}</TrialChip>}
						</PlanChipRow>
						{showUpgrade && (
							<UpgradePill type="button" onClick={openUpgrade}>{common.upgrade}</UpgradePill>
						)}
					</PlanChips>
				</IdentityRow>

				<Divider/>

				{/* Payment problem (ui#1778, was the floating banner of ui#1188/ui#1765): shown while
				    /account reports billingStatus past_due — invoice failed, Stripe dunning running,
				    access NOT cut. Links to the existing billing portal; the avatar's PastDueBadge is
				    what draws the user here. role="status" so the warning is announced when the menu
				    opens, not hunted for. */}
				{user.isPastDue() && (
					<>
						<PastDueNotice role="status">
							{/* Its own element, not a bare text node: the message must be exact-matchable on
							    its own (the e2e spec, and any consumer of the status region) without the
							    button label bleeding into the same accessible text. */}
							<span>{banner.pastDue}</span>
							{/* No `size` here: it used to say `small`, which was MUI's own value and had never
							    reached the rendered button. `BaseButton.styles.ts` restates padding, font and
							    min-height at a specificity that wins over MUI's size variant, so this control
							    has always stood at the full ACTION_CONTROL_HEIGHT_PX. Dropped rather than
							    translated to the primitive's `compact`: that would be a real change to this
							    banner's appearance, which is not what ui#1922 was asked for. */}
							<BaseButton variant="gold" disableElevation
								onClick={() => openBillingPortal()}>
								{banner.updateCard}
							</BaseButton>
						</PastDueNotice>
						<Divider/>
					</>
				)}

				<UsageBlock>
					<UsageLabel>{account.menu.totalSent}</UsageLabel>
					{usage.status === "error"
						? <UsageCaption>{account.menu.usageUnavailable}</UsageCaption>
						: <UsageValue>{usage.status === "loaded" ? formatBytes(usage.totalBytes, locale) : "…"}</UsageValue>}
					<UsageCaption>{capCaption}</UsageCaption>
				</UsageBlock>

				<Divider/>

				{/* The pill items the top bar gave up at this width (ui#451). Rendered at every width and
				    revealed by media query, so the swap needs no `matchMedia` and cannot flicker on
				    hydration; above the widest threshold the whole group — divider included — is hidden. */}
				<CollapsedNavSection>
					<List component="nav" aria-label={nav.moreAria} disablePadding>
						{collapsedDestinations.map(({destination, collapse}) => (
							<CollapsedNavRow
								key={destination.path}
								$collapse={collapse}
								disabled={destination.disabled}
								onClick={() => goTo(destination.path)}
							>
								{/* The pill's own glyph for this destination, from the shared table — the item
								    moved surface, it did not become a different thing. */}
								<RowIcon>{DESTINATION_ICONS[destination.key]}</RowIcon>
								{destination.label}
							</CollapsedNavRow>
						))}
						<CollapsedNavRow $collapse="branding" onClick={openBranding}>
							<RowIcon><BrandingIcon/></RowIcon>
							{nav.branding}
						</CollapsedNavRow>
					</List>
					<Divider/>
				</CollapsedNavSection>

				{/* Language left this menu (ui#812): it lives in the Resources dropdown for BOTH auth
				    states now, so the picker no longer moves on login. */}

				{/* Quick actions in the one order {@link profileMenuActions} defines, Logout last (#520).
				    The Upgrade row keeps its accented treatment (ui#167, ui#189): one click from the
				    chrome to the PricingDialog. Advertise with us (epic #441) is offered to every
				    signed-in tier, Free included — a takeover is a purchase, not a plan entitlement. */}
				<List component="nav" aria-label={account.menu.actionsAria} disablePadding>
					{actions.map(action => {
						const Row = action.key === "upgrade" ? UpgradeRow : MenuRow;
						return (
							<Row key={action.key} onClick={actionHandlers[action.key]}>
								<RowIcon>{actionIcons[action.key]}</RowIcon>
								{action.label}
							</Row>
						);
					})}
				</List>
			</Popover>

			{/* Signed-in upgrade flow, mounted exactly as the logged-out NavBar branch does (ui#167) for
			    tiers below Premium. Siblings of the Popover so they
			    outlive its dismissal; the checkout dialog stacks over the sheet on a paid CTA. */}
			{showUpgrade && (
				<>
					<PricingDialog
						open={pricingOpen}
						onClose={() => setPricingOpen(false)}
						currentPlanId={plan}
						onUpgrade={onUpgrade}
					/>
					<UpgradeCheckoutDialog checkout={checkout} onClose={closeCheckout}/>
					<PlanChangeDialog {...planChange}/>
				</>
			)}
		</Root>
	);
}
